import React, { useState, useEffect, useCallback } from 'react';
import API from '../services/api';
import { 
  Modal, 
  Button, 
  Form, 
  Pagination, 
  Input, 
  Table, 
  Tag, 
  message, 
  Dropdown, 
  Space, 
  Card, 
  Row, 
  Col, 
  Typography, 
  Checkbox, 
  Select, 
  DatePicker,
  Spin,
  Empty,
  Badge,
  Popconfirm,
  Tooltip
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined, 
  StarFilled, 
  StarOutlined, 
  FilterOutlined, 
  CopyOutlined, 
  MailOutlined, 
  UserOutlined, 
  CheckOutlined 
} from '@ant-design/icons';
import dayjs from 'dayjs';
import './SystemVersionManagement.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const SystemVersionManagement = () => {
  const [systemInfos, setSystemInfos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [versionTypeFilter, setVersionTypeFilter] = useState('');
  const [versionTypes, setVersionTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSystemInfo, setTotalSystemInfo] = useState(0);
  const [limit] = useState(10);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ 
    show: false, 
    systemInfo: null 
  });
  const [serverDate, setServerDate] = useState('');

  // 发送通知相关状态
  const [notificationModal, setNotificationModal] = useState({
    show: false,
    version: null,
    users: [],
    selectedUsers: [],
    loading: false,
    sending: false,
    result: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      pages: 0
    }
  });
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userLimit] = useState(20);

  // 表单状态
  const [formData, setFormData] = useState({
    version: '',
    versionType: 'release',
    updateLog: '',
    isActive: false,
    releaseDate: ''
  });

  // 获取服务器日期
  const fetchServerDate = useCallback(async () => {
    try {
      const response = await API.get('/api/update/current-date');
      const currentDate = response.data.currentDate;
      setServerDate(currentDate);
      return currentDate;
    } catch (error) {
      console.error('获取服务器日期失败:', error);
      // 如果获取服务器日期失败，回退到本地日期
      return getTodayLocalDate();
    }
  }, []);

  // 获取本地日期的辅助函数
  const getTodayLocalDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 获取当前日期（优先使用服务器日期）
  const getCurrentDate = async () => {
    if (serverDate) {
      return serverDate;
    }
    return await fetchServerDate();
  };

  // 格式化日期为本地日期字符串（用于表单输入）
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    // 如果是ISO格式的日期字符串，直接提取日期部分
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    
    // 如果是简单的日期字符串，直接返回
    return dateString;
  };

  // 版本类型配置
  const versionTypeConfig = {
    alpha: { label: '开发版', color: 'danger', icon: '🔥' },
    beta: { label: '测试版', color: 'warning', icon: '⚡' },
    rc: { label: '候选版', color: 'info', icon: '🚀' },
    sp: { label: '补丁包', color: 'secondary', icon: '🔧' },
    release: { label: '正式版', color: 'success', icon: '✅' }
  };

  // 版本号比较函数
  const compareVersions = (a, b) => {
    // 移除 'v' 前缀并获取纯版本号字符串
    const versionA = a.version.replace(/^v/i, '');
    const versionB = b.version.replace(/^v/i, '');
    
    // 分割版本号为数字数组
    const parseVersion = (version) => {
      // 检查是否是sp版本（包含-数字后缀）
      const spMatch = version.match(/^(.+)-(\d+)$/);
      let mainVersion = version;
      let spNumber = 0;
      
      if (spMatch) {
        mainVersion = spMatch[1];
        spNumber = parseInt(spMatch[2]);
      }
      
      // 分离主版本号和预发布后缀
      const [main, ...suffixes] = mainVersion.split(/[-+]/);
      const numbers = main.split('.').map(num => parseInt(num) || 0);
      
      // 确保有三个版本号段 [major, minor, patch]
      while (numbers.length < 3) {
        numbers.push(0);
      }
      
      // 确定预发布类型的优先级
      const suffix = suffixes.join('-').toLowerCase();
      let priority = 1000; // 正式版本最高优先级
      
      if (suffix.includes('alpha')) priority = 1;
      else if (suffix.includes('beta')) priority = 2;
      else if (suffix.includes('rc')) priority = 3;
      else if (suffix.includes('sp')) priority = 4;
      
      return { numbers, priority, spNumber };
    };
    
    const versionObjA = parseVersion(versionA);
    const versionObjB = parseVersion(versionB);
    
    // 逐级比较主版本号：major -> minor -> patch
    for (let i = 0; i < 3; i++) {
      if (versionObjA.numbers[i] !== versionObjB.numbers[i]) {
        // 降序排列：较大的版本号排在前面
        return versionObjB.numbers[i] - versionObjA.numbers[i];
      }
    }
    
    // 主版本号相同时，比较预发布优先级
    if (versionObjA.priority !== versionObjB.priority) {
      // 较高优先级（正式版）排在前面
      return versionObjB.priority - versionObjA.priority;
    }
    
    // 如果都是sp版本且主版本号相同，按sp序号降序排列（较大的sp序号在前）
    if (versionObjA.priority === 4 && versionObjB.priority === 4) {
      return versionObjB.spNumber - versionObjA.spNumber;
    }
    
    return 0;
  };

  // 获取版本类型列表
  const fetchVersionTypes = useCallback(async () => {
    try {
      const response = await API.get('/api/admin/system-info/version-types');
      setVersionTypes(response.data.versionTypes || []);
    } catch (error) {
      console.error('获取版本类型列表失败:', error);
      // 设置默认版本类型
      setVersionTypes([
        { value: 'alpha', label: '开发版 (Alpha)', description: '早期开发版本，功能不完整，可能存在较多问题' },
        { value: 'beta', label: '测试版 (Beta)', description: '功能基本完成，正在进行测试的版本' },
        { value: 'rc', label: '候选版 (Release Candidate)', description: '即将正式发布的候选版本，功能完整' },
        { value: 'sp', label: '补丁包 (Service Pack)', description: '针对正式版的功能更新和Bug修复包' },
        { value: 'release', label: '正式版 (Release)', description: '经过充分测试的正式发布版本' }
      ]);
    }
  }, []);

  // 获取系统版本列表
  const fetchSystemInfos = useCallback(async () => {
    try {
      const params = { 
        page, 
        limit, 
        search: searchTerm 
      };
      
      // 添加版本类型筛选
      if (versionTypeFilter) {
        params.versionType = versionTypeFilter;
      }
      
      const response = await API.get('/api/admin/system-info', { params });
      
      // 获取数据并按版本号排序
      let systemInfoList = response.data.systemInfoList || [];
      
      // 按版本号降序排序（不再优先考虑活跃版本）
      systemInfoList.sort((a, b) => {
        return compareVersions(a, b);
      });
      
      setSystemInfos(systemInfoList);
      setTotalSystemInfo(response.data.totalSystemInfo || 0);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('获取系统版本列表失败:', error);
      showAlert('获取系统版本列表失败', 'danger');
    }
  }, [page, limit, searchTerm, versionTypeFilter]);

  useEffect(() => {
    fetchVersionTypes();
    fetchServerDate(); // 初始化时获取服务器日期
  }, [fetchVersionTypes, fetchServerDate]);

  useEffect(() => {
    fetchSystemInfos();
  }, [fetchSystemInfos]);

  // 处理搜索输入变化
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  // 处理搜索按钮点击
  const handleSearch = () => {
    setSearchTerm(inputValue);
    setPage(1);
  };



  // 处理版本类型筛选
  const handleVersionTypeFilter = (type) => {
    setVersionTypeFilter(type);
    setPage(1);
  };

  // 显示提示
  const showAlert = (msg, type = 'info') => {
    if (type === 'success') {
      message.success(msg);
    } else if (type === 'danger' || type === 'error') {
      message.error(msg);
    } else if (type === 'warning') {
      message.warning(msg);
    } else {
      message.info(msg);
    }
  };

  // 打开创建/编辑模态框
  const handleShowModal = async (systemInfo = null) => {
    if (systemInfo) {
      setCurrentVersion(systemInfo);
      setFormData({
        version: systemInfo.version,
        versionType: systemInfo.versionType || 'release',
        updateLog: systemInfo.updateLog,
        isActive: systemInfo.isActive,
        releaseDate: formatDateForInput(systemInfo.releaseDate)
      });
      setIsEditing(true);
    } else {
      setCurrentVersion(null);
      const currentDate = await getCurrentDate();
      setFormData({
        version: '',
        versionType: 'release',
        updateLog: '',
        isActive: false,
        releaseDate: currentDate
      });
      setIsEditing(false);
    }
    setShowModal(true);
  };

  // 复用版本
  const handleCopyVersion = async (systemInfo) => {
    setCurrentVersion(null); // 设为null表示是创建新版本
    const currentDate = await getCurrentDate();
    setFormData({
      version: systemInfo.version, // 复用版本号，用户可以在此基础上修改
      versionType: systemInfo.versionType || 'release', // 复用版本类型
      updateLog: systemInfo.updateLog, // 复用更新日志作为模板
      isActive: false, // 新版本默认非活跃
      releaseDate: currentDate // 使用服务器当前日期
    });
    setIsEditing(false); // 设为创建模式
    setShowModal(true);
  };

  // 关闭模态框
  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentVersion(null);
    setFormData({
      version: '',
      versionType: 'release',
      updateLog: '',
      isActive: false,
      releaseDate: ''
    });
  };

  // 处理表单输入变化
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // 生成SP版本号
  const generateSpVersionNumber = async (baseVersion) => {
    try {
      // 统一处理版本号格式：移除v前缀和sp后缀，获取基础版本号
      const normalizeVersion = (version) => {
        return version.replace(/^v/i, '').replace(/-\d+$/, '');
      };
      
      const cleanVersion = normalizeVersion(baseVersion);
      
      // 获取所有已存在的版本信息
      const response = await API.get('/api/admin/system-info', { 
        params: { page: 1, limit: 1000 } // 获取所有版本用于检查
      });
      
      const existingVersions = response.data.systemInfoList || [];
      
      // 筛选出相同基础版本号且类型为sp的版本
      const spVersions = existingVersions.filter(v => {
        const versionBase = normalizeVersion(v.version);
        return versionBase === cleanVersion && v.versionType === 'sp';
      });
      
      // 如果是编辑模式，需要排除当前正在编辑的版本
      const filteredSpVersions = spVersions.filter(v => {
        if (currentVersion && currentVersion._id) {
          return v._id !== currentVersion._id;
        }
        return true;
      });
      
      // 提取所有sp后缀数字
      const spNumbers = filteredSpVersions.map(v => {
        const match = v.version.match(/-(\d+)$/);
        return match ? parseInt(match[1]) : 0;
      }).filter(n => n > 0);
      
      // 计算下一个序号
      let nextNumber = 1;
      if (spNumbers.length > 0) {
        nextNumber = Math.max(...spNumbers) + 1;
      }
      
      // 返回版本号时保持原输入格式的v前缀（如果有的话）
      const hasVPrefix = /^v/i.test(baseVersion);
      const finalVersion = hasVPrefix ? `v${cleanVersion}-${nextNumber}` : `${cleanVersion}-${nextNumber}`;
      
      return finalVersion;
    } catch (error) {
      console.error('生成SP版本号失败:', error);
      // 如果获取失败，默认返回-1，保持原输入格式
      const hasVPrefix = /^v/i.test(baseVersion);
      const cleanVersion = baseVersion.replace(/^v/i, '').replace(/-\d+$/, '');
      return hasVPrefix ? `v${cleanVersion}-1` : `${cleanVersion}-1`;
    }
  };

  // 提交表单
  const handleSubmit = async () => {
    if (!formData.version.trim() || !formData.updateLog.trim()) {
      showAlert('版本号和更新日志不能为空', 'danger');
      return;
    }

    try {
      let finalFormData = { ...formData };
      
      // 如果是创建新版本且版本类型为sp，自动处理版本号
      if (!isEditing && formData.versionType === 'sp') {
        const spVersion = await generateSpVersionNumber(formData.version);
        finalFormData.version = spVersion;
      }

      if (isEditing) {
        await API.put(`/api/admin/system-info/${currentVersion._id}`, finalFormData);
        showAlert('系统版本更新成功', 'success');
      } else {
        await API.post('/api/admin/system-info', finalFormData);
        showAlert('系统版本创建成功', 'success');
      }
      handleCloseModal();
      fetchSystemInfos();
    } catch (error) {
      console.error('操作失败:', error);
      const errorMessage = error.response?.data?.message || '操作失败';
      showAlert(errorMessage, 'danger');
    }
  };

  // 确认删除系统版本
  const confirmDeleteVersion = async () => {
    const systemInfo = deleteConfirmModal.systemInfo;
    if (!systemInfo) return;

    try {
      await API.delete(`/api/admin/system-info/${systemInfo._id}`);
      showAlert('系统版本删除成功', 'success');
      setDeleteConfirmModal({ show: false, systemInfo: null });
      fetchSystemInfos();
    } catch (error) {
      console.error('删除系统版本失败:', error);
      const errorMessage = error.response?.data?.message || '删除系统版本失败';
      showAlert(errorMessage, 'error');
    }
  };

  // 设置活跃版本
  const handleSetActiveVersion = async (systemInfo) => {
    if (systemInfo.isActive) {
      showAlert('该版本已经是活跃版本', 'info');
      return;
    }

    try {
      await API.put(`/api/admin/system-info/${systemInfo._id}/activate`);
      showAlert(`版本 ${systemInfo.version} 已设置为活跃版本`, 'success');
      fetchSystemInfos();
    } catch (error) {
      console.error('设置活跃版本失败:', error);
      const errorMessage = error.response?.data?.message || '设置活跃版本失败';
      showAlert(errorMessage, 'danger');
    }
  };

  // 格式化日期
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-CN');
  };

  // 格式化发布日期
  const formatReleaseDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-CN');
  };

  // 渲染版本类型徽章
  const renderVersionTypeBadge = (versionType) => {
    const config = versionTypeConfig[versionType] || { label: '未知', color: 'default', icon: '❓' };
    const colorMap = {
      danger: 'volcano',
      warning: 'orange',
      info: 'blue',
      secondary: 'default',
      success: 'green'
    };
    return (
      <Tag color={colorMap[config.color] || 'default'} className="version-type-badge">
        <span style={{ marginRight: 4 }}>{config.icon}</span>
        {config.label}
      </Tag>
    );
  };

  // 获取可发送通知的用户列表
  const fetchNotificationUsers = useCallback(async (page = 1, keyword = '') => {
    try {
      setNotificationModal(prev => ({ ...prev, loading: true }));
      
      const response = await API.get('/api/admin/notification-users', {
        params: {
          page,
          limit: userLimit,
          keyword: keyword.trim()
        }
      });

      if (response.data.code === 0) {
        setNotificationModal(prev => ({
          ...prev,
          users: response.data.data.users,
          pagination: response.data.data.pagination,
          loading: false
        }));
      } else {
        showAlert(response.data.msg || '获取用户列表失败', 'danger');
        setNotificationModal(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('获取用户列表失败:', error);
      showAlert('获取用户列表失败', 'danger');
      setNotificationModal(prev => ({ ...prev, loading: false }));
    }
  }, [userLimit]);

  // 发送重大更新通知
  const sendUpdateNotification = async () => {
    try {
      setNotificationModal(prev => ({ ...prev, sending: true }));

      const requestData = {
        versionId: notificationModal.version._id
      };

      // 如果有选中的用户，则发送给指定用户
      if (notificationModal.selectedUsers.length > 0) {
        requestData.userIds = notificationModal.selectedUsers;
      }
      // 否则发送给所有用户（后端会自动处理）

      const response = await API.post('/api/admin/send-major-update-notification', requestData);

      if (response.data.code === 0) {
        setNotificationModal(prev => ({
          ...prev,
          sending: false,
          result: response.data.data
        }));
        showAlert('重大更新通知发送成功', 'success');
      } else {
        showAlert(response.data.msg || '发送通知失败', 'danger');
        setNotificationModal(prev => ({ ...prev, sending: false }));
      }
    } catch (error) {
      console.error('发送通知失败:', error);
      showAlert(error.response?.data?.msg || '发送通知失败', 'danger');
      setNotificationModal(prev => ({ ...prev, sending: false }));
    }
  };

  // 显示发送通知模态框
  const showNotificationModal = async (systemInfo) => {
    setNotificationModal({
      show: true,
      version: systemInfo,
      users: [],
      selectedUsers: [],
      loading: false,
      sending: false,
      result: null,
      pagination: {
        page: 1,
        limit: userLimit,
        total: 0,
        pages: 0
      }
    });
    setUserSearchTerm('');
    // 自动加载用户列表
    await fetchNotificationUsers(1, '');
  };

  // 关闭发送通知模态框
  const closeNotificationModal = () => {
    setNotificationModal({
      show: false,
      version: null,
      users: [],
      selectedUsers: [],
      loading: false,
      sending: false,
      result: null,
      pagination: {
        page: 1,
        limit: userLimit,
        total: 0,
        pages: 0
      }
    });
    setUserSearchTerm('');
  };

  // 处理用户选择
  const handleUserSelection = (userId, checked) => {
    setNotificationModal(prev => ({
      ...prev,
      selectedUsers: checked
        ? [...prev.selectedUsers, userId]
        : prev.selectedUsers.filter(id => id !== userId)
    }));
  };

  // 全选/取消全选用户
  const handleSelectAllUsers = (checked) => {
    setNotificationModal(prev => ({
      ...prev,
      selectedUsers: checked
        ? prev.users.map(user => user._id)
        : []
    }));
  };

  // 搜索用户
  const handleUserSearch = () => {
    fetchNotificationUsers(1, userSearchTerm);
  };

  // 处理用户列表分页
  const handleUserPageChange = (page) => {
    fetchNotificationUsers(page, userSearchTerm);
  };



  return (
    <div className="system-version-management">
      {/* 页面头部 */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>系统版本管理</Title>
      </div>

      {/* 统计和搜索区域 */}
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <div>
              <Text type="secondary">
                总版本数量：<Text strong style={{ color: '#1890ff' }}>{totalSystemInfo}</Text>
                {versionTypeFilter && (
                  <span style={{ marginLeft: 8 }}>
                    筛选类型：{renderVersionTypeBadge(versionTypeFilter)}
                  </span>
                )}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                📋 排序规则：按版本号降序排列（最新版本在顶部）
              </Text>
            </div>
          </Col>
          <Col>
            <Space>
              <Input.Group compact>
                <Input
                  style={{ width: 300 }}
                  placeholder="搜索版本号或更新日志..."
                  value={inputValue}
                  onChange={handleInputChange}
                  onPressEnter={handleSearch}
                  suffix={<SearchOutlined onClick={handleSearch} style={{ cursor: 'pointer' }} />}
                />
              </Input.Group>
              
              {/* 版本类型筛选下拉菜单 */}
              <Dropdown 
                menu={{
                  items: [
                    {
                      key: '',
                      label: '全部类型',
                      onClick: () => handleVersionTypeFilter('')
                    },
                    { type: 'divider' },
                    ...Object.entries(versionTypeConfig).map(([type, config]) => ({
                      key: type,
                      label: (
                        <span>
                          <span style={{ marginRight: 8 }}>{config.icon}</span>
                          {config.label}
                        </span>
                      ),
                      onClick: () => handleVersionTypeFilter(type)
                    }))
                  ]
                }}
                trigger={['click']}
              >
                <Button>
                  <FilterOutlined />
                  {versionTypeFilter ? versionTypeConfig[versionTypeFilter]?.label : '筛选类型'}
                </Button>
              </Dropdown>
            </Space>
          </Col>
        </Row>

        <Row justify="space-between" align="middle">
          <Col>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <span style={{
                display: 'inline-block',
                width: 12,
                height: 12,
                backgroundColor: 'rgba(82, 196, 26, 0.3)',
                border: '2px solid #52c41a',
                borderRadius: 2,
                marginRight: 4
              }}></span>
              活跃版本
              <span style={{
                display: 'inline-block',
                width: 12,
                height: 12,
                backgroundColor: '#f5f5f5',
                border: '2px solid #d9d9d9',
                borderRadius: 2,
                marginLeft: 16,
                marginRight: 4
              }}></span>
              非活跃版本
            </Text>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={async () => await handleShowModal()}
            >
              创建版本
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 版本列表 */}
      <Card>
        <Table
          dataSource={systemInfos}
          rowKey="_id"
          pagination={false}
          scroll={{ x: 1200 }}
          rowClassName={(record) => record.isActive ? 'active-row' : ''}
          onRow={(record) => ({
            style: {
              backgroundColor: record.isActive ? 'rgba(82, 196, 26, 0.1)' : 'transparent',
              borderLeft: record.isActive ? '4px solid #52c41a' : '4px solid transparent'
            }
          })}
          columns={[
            {
              title: (
                <span>
                  版本号
                  <Tooltip title="按版本号降序排列">
                    <span style={{ marginLeft: 4 }}>↓</span>
                  </Tooltip>
                </span>
              ),
              dataIndex: 'version',
              key: 'version',
              width: 150,
              render: (version, record) => (
                <Space>
                  {record.isActive && <StarFilled style={{ color: '#faad14' }} />}
                  <Text strong>{version}</Text>
                </Space>
              )
            },
            {
              title: '版本类型',
              dataIndex: 'versionType',
              key: 'versionType',
              width: 120,
              render: (versionType) => renderVersionTypeBadge(versionType || 'release')
            },
            {
              title: '更新日志',
              dataIndex: 'updateLog',
              key: 'updateLog',
              ellipsis: true,
              render: (text) => (
                <Tooltip title={text}>
                  <Text style={{ maxWidth: 300 }}>{text}</Text>
                </Tooltip>
              )
            },
            {
              title: '状态',
              dataIndex: 'isActive',
              key: 'isActive',
              width: 100,
              align: 'center',
              render: (isActive) => (
                <Badge 
                  status={isActive ? 'success' : 'error'} 
                  text={isActive ? '活跃' : '非活跃'}
                />
              )
            },
            {
              title: '发布日期',
              dataIndex: 'releaseDate',
              key: 'releaseDate',
              width: 120,
              align: 'center',
              render: (date) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {formatReleaseDate(date)}
                </Text>
              )
            },
            {
              title: '创建时间',
              dataIndex: 'createdAt',
              key: 'createdAt',
              width: 150,
              align: 'center',
              render: (date) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {formatDate(date)}
                </Text>
              )
            },
            {
              title: '操作',
              key: 'action',
              width: 280,
              align: 'center',
              render: (_, record) => (
                <Space>
                  <Tooltip title={record.isActive ? '当前活跃版本' : '设为活跃版本'}>
                    <Button
                      type={record.isActive ? 'default' : 'primary'}
                      size="small"
                      icon={record.isActive ? <StarFilled /> : <StarOutlined />}
                      onClick={() => handleSetActiveVersion(record)}
                      disabled={record.isActive}
                    />
                  </Tooltip>
                  <Tooltip title="发送更新通知邮件">
                    <Button
                      type="primary"
                      size="small"
                      icon={<MailOutlined />}
                      onClick={() => showNotificationModal(record)}
                    />
                  </Tooltip>
                  <Tooltip title="编辑">
                    <Button
                      type="default"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={async () => await handleShowModal(record)}
                    />
                  </Tooltip>
                  <Tooltip title="复用此版本创建新版本">
                    <Button
                      type="default"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={async () => await handleCopyVersion(record)}
                    />
                  </Tooltip>
                  <Popconfirm
                    title="确定要删除这个版本吗？"
                    description="此操作不可撤销，请谨慎操作！"
                    onConfirm={() => confirmDeleteVersion()}
                    onOpenChange={(open) => {
                      if (open) {
                        setDeleteConfirmModal({ show: true, systemInfo: record });
                      }
                    }}
                    disabled={record.isActive}
                  >
                    <Tooltip title="删除">
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        disabled={record.isActive}
                      />
                    </Tooltip>
                  </Popconfirm>
                </Space>
              )
            }
          ]}
        />
      </Card>

      {/* 分页 */}
      <Row justify="space-between" align="middle" style={{ marginTop: 24 }}>
        <Col>
          <Pagination
            current={page}
            total={totalSystemInfo}
            pageSize={limit}
            showSizeChanger={false}
            showQuickJumper
            showTotal={(total, range) => 
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
            }
            onChange={(page) => setPage(page)}
          />
        </Col>
        <Col>
          <Text type="secondary">
            当前第 {page} 页，共 {totalPages} 页
          </Text>
        </Col>
      </Row>

      {/* 创建/编辑版本模态框 */}
      <Modal
        title={isEditing ? '编辑版本' : '创建版本'}
        open={showModal}
        onCancel={handleCloseModal}
        width={800}
        footer={[
          <Button key="cancel" onClick={handleCloseModal}>
            取消
          </Button>,
          <Button key="submit" type="primary" onClick={handleSubmit}>
            {isEditing ? '更新' : '创建'}
          </Button>
        ]}
      >
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="版本号">
                <Input
                  name="version"
                  value={formData.version}
                  onChange={handleFormChange}
                  placeholder="例如：v1.0.0"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                label="版本类型"
                help={
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {versionTypes.find(t => t.value === formData.versionType)?.description}
                    </Text>
                    {formData.versionType === 'sp' && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 12, color: '#1890ff' }}>
                          💡 SP版本将在创建时自动添加序号（如：v1.0.0 → v1.0.0-1）
                        </Text>
                      </div>
                    )}
                  </div>
                }
              >
                <Select
                  value={formData.versionType}
                  onChange={(value) => handleFormChange({ target: { name: 'versionType', value } })}
                  style={{ width: '100%' }}
                >
                  {versionTypes.map((type) => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="更新日志">
            <TextArea
              rows={5}
              name="updateLog"
              value={formData.updateLog}
              onChange={handleFormChange}
              placeholder="请输入此版本的更新内容..."
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="发布日期">
                <DatePicker
                  style={{ width: '100%' }}
                  value={formData.releaseDate ? dayjs(formData.releaseDate) : null}
                  onChange={(date, dateString) => 
                    handleFormChange({ target: { name: 'releaseDate', value: dateString } })
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="设置">
                <div style={{ paddingTop: 8 }}>
                  <Checkbox
                    checked={formData.isActive}
                    onChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
                  >
                    设为活跃版本
                  </Checkbox>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      设为活跃版本将自动取消其他版本的活跃状态
                    </Text>
                  </div>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>



      {/* 发送更新通知模态框 */}
      <Modal
        title={
          <Space>
            <MailOutlined style={{ color: '#1890ff' }} />
            发送重大更新通知
          </Space>
        }
        open={notificationModal.show}
        onCancel={closeNotificationModal}
        width={800}
        footer={notificationModal.result ? (
          <Button type="primary" onClick={closeNotificationModal}>
            关闭
          </Button>
        ) : (
          [
            <Button key="cancel" onClick={closeNotificationModal}>
              取消
            </Button>,
            <Button 
              key="send"
              type="primary" 
              icon={<MailOutlined />}
              loading={notificationModal.sending}
              onClick={sendUpdateNotification}
            >
              {notificationModal.sending ? '发送中...' : (
                <>
                  发送通知
                  {notificationModal.selectedUsers.length > 0 && 
                    ` (${notificationModal.selectedUsers.length} 个用户)`
                  }
                </>
              )}
            </Button>
          ]
        )}
      >
        {notificationModal.result ? (
          // 显示发送结果
          <div>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <CheckOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
              <Title level={4}>通知发送完成</Title>
            </div>
            
            <Card size="small" style={{ marginBottom: 16 }}>
              <Title level={5}>版本信息：</Title>
              <Text>
                <strong>版本号：</strong>{notificationModal.result.versionInfo.version}
              </Text>
              <br />
              <Text>
                <strong>版本类型：</strong>{renderVersionTypeBadge(notificationModal.result.versionInfo.versionType || 'release')}
              </Text>
              <br />
              <Text>
                <strong>发布日期：</strong>{formatReleaseDate(notificationModal.result.versionInfo.releaseDate)}
              </Text>
            </Card>

            <Row gutter={16} style={{ textAlign: 'center', marginBottom: 16 }}>
              <Col span={8}>
                <Card size="small">
                  <Title level={3} style={{ color: '#1890ff', margin: 0 }}>
                    {notificationModal.result.totalUsers}
                  </Title>
                  <Text type="secondary">目标用户</Text>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <Title level={3} style={{ color: '#52c41a', margin: 0 }}>
                    {notificationModal.result.successCount}
                  </Title>
                  <Text type="secondary">发送成功</Text>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <Title level={3} style={{ color: '#ff4d4f', margin: 0 }}>
                    {notificationModal.result.failedCount}
                  </Title>
                  <Text type="secondary">发送失败</Text>
                </Card>
              </Col>
            </Row>

            {notificationModal.result.failedCount > 0 && notificationModal.result.failedUsers && (
              <Card size="small" type="inner" style={{ borderColor: '#faad14' }}>
                <Title level={5}>发送失败的用户：</Title>
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {notificationModal.result.failedUsers.map((user, index) => (
                    <div key={index} style={{ marginBottom: 8, fontSize: 12 }}>
                      <Text strong>{user.username}</Text> ({user.email}) - {user.reason}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        ) : (
          // 显示用户选择界面
          <div>
            {notificationModal.version && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <Title level={5}>将要发送的版本信息：</Title>
                <Text>
                  <strong>版本号：</strong>{notificationModal.version.version}
                </Text>
                <br />
                <Text>
                  <strong>版本类型：</strong>{renderVersionTypeBadge(notificationModal.version.versionType || 'release')}
                </Text>
                <br />
                <Text>
                  <strong>发布日期：</strong>{formatReleaseDate(notificationModal.version.releaseDate)}
                </Text>
              </Card>
            )}

            <div style={{ marginBottom: 16 }}>
              <Title level={5}>
                <UserOutlined style={{ marginRight: 8 }} />
                选择通知用户
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                  (不选择任何用户将发送给所有活跃用户)
                </Text>
              </Title>
              
              {/* 用户搜索 */}
              <div style={{ marginBottom: 16 }}>
                <Input.Group compact>
                  <Input
                    style={{ width: 'calc(100% - 80px)' }}
                    placeholder="搜索用户名或邮箱..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    onPressEnter={handleUserSearch}
                  />
                  <Button type="primary" icon={<SearchOutlined />} onClick={handleUserSearch} />
                </Input.Group>
              </div>

              {/* 全选控制 */}
              <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                <Col>
                  <Checkbox
                    checked={notificationModal.selectedUsers.length === notificationModal.users.length && notificationModal.users.length > 0}
                    onChange={(e) => handleSelectAllUsers(e.target.checked)}
                  >
                    全选当前页 ({notificationModal.users.length} 个用户)
                  </Checkbox>
                </Col>
                {notificationModal.pagination.total > 0 && (
                  <Col>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      共 {notificationModal.pagination.total} 个用户，第 {notificationModal.pagination.page} / {notificationModal.pagination.pages} 页
                    </Text>
                  </Col>
                )}
              </Row>

              {/* 用户列表 */}
              <div style={{ 
                maxHeight: 300, 
                overflowY: 'auto', 
                border: '1px solid #d9d9d9', 
                borderRadius: 6,
                padding: 8
              }}>
                {notificationModal.loading ? (
                  <div style={{ textAlign: 'center', padding: 16 }}>
                    <Spin size="small" style={{ marginRight: 8 }} />
                    加载用户列表...
                  </div>
                ) : notificationModal.users.length === 0 ? (
                  <Empty 
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={userSearchTerm ? '没有找到匹配的用户' : '没有可发送的用户'}
                  />
                ) : (
                  notificationModal.users.map((user) => (
                    <div key={user._id} style={{ 
                      padding: 8, 
                      borderBottom: '1px solid #f0f0f0',
                      marginBottom: 8
                    }}>
                      <Checkbox
                        checked={notificationModal.selectedUsers.includes(user._id)}
                        onChange={(e) => handleUserSelection(user._id, e.target.checked)}
                      >
                        <div>
                          <Text strong>{user.username}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>{user.email}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            最后登录: {user.lastLoginTime ? formatDate(user.lastLoginTime) : '从未登录'}
                          </Text>
                        </div>
                      </Checkbox>
                    </div>
                  ))
                )}
              </div>

              {/* 用户列表分页 */}
              {notificationModal.pagination.pages > 1 && (
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                  <Pagination
                    size="small"
                    current={notificationModal.pagination.page}
                    total={notificationModal.pagination.total}
                    pageSize={notificationModal.pagination.limit}
                    showSizeChanger={false}
                    onChange={handleUserPageChange}
                  />
                </div>
              )}

              {notificationModal.selectedUsers.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12, color: '#1890ff' }}>
                    已选择 {notificationModal.selectedUsers.length} 个用户
                  </Text>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SystemVersionManagement; 